import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Airlines Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts`
 * writes every Industry Focus entry from one sentence template. Airlines was
 * already the exception on the *copy* side — its three hero paragraphs were read
 * off the live page when the reports index was built on 2026-08-26, and
 * `reports.ts` has carried them verbatim since. What it did not have was the
 * page: it was still drawn by the shared two-column template. Asked for on
 * 2026-08-27, *"airlines focusreport"*, with four attachments — a full-page
 * screenshot of digitopia.co's Airlines Industry Focus page as the reference, the
 * cover at 2560px, the vitality chart and the report mockup. So this is the sixth
 * designed report page, and the first Industry Focus cut that arrives with **both
 * of its figures as files**.
 *
 * **Where the words come from, band by band.** The hero is the supplied copy that
 * was already in `reports.ts` — it is imported from nowhere and written out below
 * so the module reads as the page reads, and `reports.ts` now takes its `body`
 * from here instead of the other way round. Everything from the findings band
 * down was transcribed from the reference screenshot, which was a 411px-wide
 * full-page capture: the *shape* of the page was certain and the *wording* was a
 * careful reading of body copy set at a handful of pixels. Every one of those
 * bands therefore carried a `check` asking to be read against digitopia.co.
 *
 * **That reading happened on 2026-08-28**, when the page was asked for again with
 * the reference re-supplied at full width — four captures rather than one, so
 * body copy is legible this time — together with the same three figures, which
 * were already in `photo-src/` from the first round and are unchanged. Four
 * sentences came back different and are corrected below: the lede's closing
 * clause, "modern" where this page had guessed "mature", the whole first
 * paragraph of the analysis band (the live one is about simplifying core
 * architectures, and its functions are pilots, crew and control), and the figure
 * caption, which carries no company count. So the findings and analysis notes are
 * resolved — each band says at its own `check` what changed — and the only amber
 * note `daimiAirlinesChecks()` still feeds the manifest is the download one,
 * which is a decision rather than a transcription.
 *
 * **What the live page has that this page cannot.** Its five Download buttons
 * open an embedded HubSpot form. These documents are prerendered with no client
 * bundle at all (see `src/prerender.tsx`), so an embedded form script would never
 * run — and the other thirty reports on this site hand the reader the PDF from
 * our own bucket instead. So every Download button here is that PDF, and
 * `DAIMI_AL_DOWNLOAD.check` is what keeps the question of gating open.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_AL_SLUG = 'digital-and-ai-maturity-report-2026-airlines-industry'

/**
 * The hero's three paragraphs — the only copy on this page that is not a
 * transcription of the new screenshot.
 *
 * They were supplied with the reports index on 2026-08-26 and have been the
 * report's index-card description ever since; `reports.ts` reads them from here
 * now, so the search result and the page cannot drift apart. The third is
 * `FOCUS_CLOSE` word for word — the series' own closing line, which `reports.ts`
 * took from this very page in the first place. Written out rather than imported
 * so that rewording the series line for the other sixteen cuts cannot silently
 * reword the page it came from.
 */
export const DAIMI_AL_INTRO = {
  body: [
    'Airlines operate a time-critical, customer-visible value chain that has driven early adoption of digital models and data-driven decisions. However, a gap is widening between leaders who operationalize AI across their “operational heartbeat” and those stalled by fragmented, manual processes.',
    'Success requires embedding AI into critical workflows like disruption management and maintenance to ensure safety while accelerating decision-making.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card
   * in the reports grid carries — and the file attached with this request is the
   * same artwork at 2560px, so it replaced it under the same key rather than
   * landing beside it under a second name. The index card reads the same path and
   * simply gets a sharper cover out of it.
   */
  photo: {
    src: '/photo/daimi-report-2026-airlines-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Airlines Focus, February 2026',
  } satisfies Photo,
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The three bullets are one block on the live page in which each line opens with
 * a bolded label — "Core Strengths:", "The Scaling Gap:", "Operational
 * Advantage:". Split into `label` and `body` here so the label can be set rather
 * than emphasised inside a sentence, which is what a `<strong>` in the middle of
 * a paragraph gives.
 */
export const DAIMI_AL_FINDINGS = {
  heading: 'DAIMI Findings: Commercial Discipline vs. Legacy Gravity',
  lede: 'Airlines demonstrate a vitality pattern where agility (Velocity) and foundational resilience (Endurance) rise together, though most organizations currently cluster at mid-level maturity.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in established analytical routines like revenue management, demand forecasting, and loyalty ecosystems.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Progress slows significantly when integration is required across legacy cores and partner interfaces, such as ground handling and maintenance providers.',
    },
    {
      label: 'Operational Advantage',
      body: 'The sector’s inherent operational discipline and standard procedures provide a major advantage for scaling automation when paired with modern platforms.',
    },
  ],
  /**
   * Figure 1 itself, which is the file supplied with this request — the first
   * Industry Focus cut to arrive with its own vitality chart rather than with the
   * card of type `pages/DaimiAirports.tsx` stands in with. It is 1200×1185 on a
   * white ground, so it gets a white card of its own and a floor under it: a
   * scatter of forty-five marks between 2.0 and 3.5 on both axes is unreadable
   * squeezed to phone width.
   */
  photo: {
    src: '/photo/daimi-airlines-vitality-chart.webp',
    alt: 'DAIMI Vitality Chart for the airlines industry: 45 companies plotted on Digital Velocity against Digital Endurance, clustered between 2.5 and 3.5 on both axes',
  } satisfies Photo,
  /**
   * The caption, punctuated with a colon rather than a dash — asked for on
   * 2026-08-30, no dashes on the sector pages. The live page separates the two
   * halves with a dash; six of the eight focus cuts already read
   * "…Study Results: <Sector> Industry Score…", so a colon is the house
   * punctuation here and the wording is otherwise untouched.
   */
  caption: 'Figure 1: DAIMI Vitality Chart: Airlines Industry',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  /*
   * No amber note left on this band. It used to carry one asking for the lede and
   * the three findings to be read against digitopia.co, because they had been
   * transcribed off a 411px-wide capture where body copy renders a few pixels
   * tall. The 2026-08-28 request supplied that capture at full width, the reading
   * was done, and three things came back changed — the lede's closing clause, the
   * word "modern" where this page had guessed "mature", and the caption, which
   * names the chart and then the sector with no company count
   * appended. Forty-five is in the chart's own furniture and in the alt text,
   * which is where the other focus pages leave it.
   */
  check: undefined,
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_AL_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Airlines Industry',
  body: [
    'To win in 2026, airline executives must prioritize value-led modernization that simplifies core architectures to enable real-time visibility. Success requires establishing AI governance that is explicitly designed for safe speed, ensuring that new tools are actually adopted into the daily decision routines of pilots, crew, and control functions.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn operational pressure into a sustained, AI-advantaged competitive edge.',
  ],
  /** The mockup supplied with this request — the airlines cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-airlines-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Airlines Focus',
  } satisfies Photo,
  /*
   * Resolved on 2026-08-28 by the same full-width capture as the findings band.
   * The note here asked for the first paragraph in particular to be read against
   * the live page, "which names the functions AI has to reach" — and it named the
   * wrong ones. The live sentence is about value-led modernization simplifying
   * core architectures, and the functions are pilots, crew and control, not crew,
   * operations and maintenance. Both paragraphs are the live wording now.
   */
  check: undefined,
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut. They
 * are `content/reportClosing.ts` now, drawn by `components/ReportClosing.tsx` on
 * every report page but three — asked for on 2026-08-28, *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"*. The assessment-count
 * question those notes raised is asked once there instead of once per cut.
 *
 * The 2026-08-28 request restated the standing instruction about that pair — *"en
 * aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a
 * yönlendirelim"* — and there is nothing to change for it: the shared band's CTA
 * is `DAIMI_SLUG`, the Digital & AI Maturity Report 2026, and has been since it
 * was extracted. This page inherits the answer rather than restating it. The
 * reference agrees on the words too: its Global Perspective card names the 2026
 * report and calls this cut "The Airlines Focus Report", which is the `shortName`
 * `focus()` already gives it.
 *
 * Two things the reference shows that the shared band does not, both left alone
 * on purpose. The live methodology band has a photographic backdrop where this
 * one has the DAIMI wheel, and the live Global Perspective card is text only
 * where this one has the report stack beside it. Both are decisions that belong
 * to all twenty-eight report pages at once, not to this cut — changing them here
 * would mean editing the shared component and breaking the *"bire bir aynı"* the
 * band exists to hold.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_AL_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Airlines Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiAirportsChecks()` use,
 * and appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this is the
 * first focus cut that arrived with both of its figures. One note is left, and it
 * is the only one that was never a transcription: whether the download should be
 * gated, which is marketing's to answer. The findings and analysis notes were the
 * other kind and the 2026-08-28 reference reading closed them; the hero never
 * carried one, because its words were supplied rather than read off a screenshot.
 * The three entries stay listed below so resolving a band is deleting its text and
 * not editing this function.
 */
export function daimiAirlinesChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'findings', check: DAIMI_AL_FINDINGS.check },
    { section: 'analysis', check: DAIMI_AL_ANALYSIS.check },
    { section: 'download', check: DAIMI_AL_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_AL_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
